import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import "../styles/sidebar.css";
import { useAuth } from "../Contexts/AuthContext";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faUserTie,
  faClipboardCheck,
  faUsers,
  faTasks,
  faChartLine,
  faCalendarDays,
  faRightFromBracket,
  faUserInjured,
  faBoxOpen,
  faWarehouse,
  faShoppingCart,
  faUserMd,
  faHospital,
  faFileMedical,
  faCapsules,
  faUserGraduate,
  faChalkboard,
  faMoneyBill,
  faBook,
  faSquare,
  faFileInvoiceDollar,
  faBriefcase,
  faProjectDiagram,
  faWallet,
  faPercentage,
  faIndustry,
  faCheckDouble,
  faTools,
  faGavel,
  faFolderOpen,
  faHourglassHalf,
  faShieldHalved,
  faCommentDots,
  faShareNodes,
  faPhoneVolume,
  faFilter,
  faVault,
  faUserShield,
  faUserClock,
  faCalendarCheck,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

// Map backend icon strings to FontAwesome objects
const iconMapping = {
  faHome,
  faUserTie,
  faClipboardCheck,
  faUsers,
  faTasks,
  faChartLine,
  faCalendarDays,
  faRightFromBracket,
  faUserInjured,
  faBoxOpen,
  faWarehouse,
  faShoppingCart,
  faUserMd,
  faHospital,
  faFileMedical,
  faCapsules,
  faUserGraduate,
  faChalkboard,
  faMoneyBill,
  faBook,
  faFileInvoiceDollar,
  faBriefcase,
  faProjectDiagram,
  faWallet,
  faPercentage,
  faIndustry,
  faCheckDouble,
  faTools,
  faGavel,
  faFolderOpen,
  faHourglassHalf,
  faShieldHalved,
  faCommentDots,
  faShareNodes,
  faPhoneVolume,
  faFilter,
  faVault,
  faUserShield,
  faUserClock,
  faCalendarCheck,
};

export default function Sidebar({ isCollapsed, setIsCollapsed }) {
  const location = useLocation();
  const { user, navigation, onboardingComplete } = useAuth();
  const [open, setOpen] = useState(false);
  
  // Auto-scroll to active item on mount or navigation change
  useEffect(() => {
    const activeItem = document.querySelector(".sb-item.active");
    if (activeItem) {
      activeItem.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [location.pathname, navigation]);

  // Helper to get the correct icon component
  const getIcon = (iconName) => {
    return iconMapping[iconName] || faSquare;
  };

  const isSuperadmin = user?.is_superuser || user?.rbac_roles_details?.some(r => r.name === 'Superadmin');

  const orgName = isSuperadmin ? "Global Workspace" : (user?.organization_details?.name || "Global Workspace");
  const industryName = isSuperadmin ? "Superadmin" : (user?.organization_details?.industry_name || "Superadmin");
  const avatarText = isSuperadmin ? "GLO" : orgName.substring(0, 3).toUpperCase();

  return (
    <>
      {/* Mobile toggle */}
      <div
        className={`sb-toggle ${open ? "is-open" : ""}`}
        onClick={() => setOpen(!open)}
        role="button"
        aria-label="Toggle menu"
      >
        <span />
        <span />
        <span />
      </div>

      <aside className={`sb-aside ${open ? "open" : ""} ${isCollapsed ? "collapsed" : ""}`}>
        {/* Logo */}
        <div className="sb-logo-wrap">
          <div className="sb-logo-row">
            <div className="sb-logo-icon">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
            </div>
            {!isCollapsed && (
              <div style={{ flex: 1 }}>
                <div className="sb-logo-name">DoneInSeconds</div>
                <div className="sb-logo-sub">Business Platform</div>
              </div>
            )}
            
            <button 
              className="sb-collapse-btn" 
              onClick={() => setIsCollapsed(!isCollapsed)}
              title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronLeft} />
            </button>
          </div>

          {!isCollapsed && (
            <div className="sb-workspace">
              <div className="sb-workspace-left" style={{ width: '100%' }}>
                <div className="sb-ws-avatar">{avatarText}</div>
                <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <span className="sb-ws-name" title={orgName}>{orgName}</span>
                  <span style={{ fontSize: '10px', opacity: 0.6 }}>{industryName}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Grouped Menu */}
        <nav className="sb-menu">
          {Object.entries(
            navigation.reduce((acc, item) => {
              const tag = item.tag || "General";
              if (!acc[tag]) acc[tag] = [];
              acc[tag].push(item);
              return acc;
            }, {})
          ).map(([tag, items]) => (
            <div key={tag} className="sb-category-group">
              <div className="sb-category-title">{tag}</div>
              {items.map((item) => {
                const isActive = location.pathname === item.path;
                const isSetupModule = item.codename === 'org_profile';
                const isDisabled = !onboardingComplete && !isSetupModule && item.codename !== 'dashboard';

                return (
                  <Link
                    key={item.codename}
                    to={isDisabled ? "#" : item.path}
                    className={`sb-item ${isActive ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
                    onClick={(e) => {
                      if (isDisabled) {
                        e.preventDefault();
                        return;
                      }
                      setOpen(false);
                    }}
                    style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  >
                    <div className="sb-item-icon">
                      <FontAwesomeIcon icon={getIcon(item.icon_name)} />
                    </div>
                    <div className="sb-item-label">
                      <span className="sb-item-name">{item.name}</span>
                      <span className="sb-item-tag">{isDisabled ? "Setup Required" : item.tag}</span>
                    </div>
                    {!isDisabled && <span className="sb-item-pill">{isActive ? "●" : "○"}</span>}
                    {isDisabled && <FontAwesomeIcon icon={faSquare} style={{ fontSize: '8px', opacity: 0.3 }} />}
                  </Link>
                );
              })}
            </div>
          ))}

          {/* System Section */}
          <div className="sb-category-group">
            <div className="sb-category-title">System</div>
            <Link
              to="/self-analysis"
              className={`sb-item ${location.pathname === "/self-analysis" ? "active" : ""}`}
              onClick={() => setOpen(false)}
            >
              <div className="sb-item-icon">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="sb-item-label">
                <span className="sb-item-name">Self Analysis</span>
                <span className="sb-item-tag">Performance</span>
              </div>
            </Link>

            <Link
              to="/profile"
              className={`sb-item ${location.pathname === "/profile" ? "active" : ""}`}
              onClick={() => setOpen(false)}
            >
              <div className="sb-item-icon">
                <FontAwesomeIcon icon={faUserTie} />
              </div>
              <div className="sb-item-label">
                <span className="sb-item-name">My Profile</span>
                <span className="sb-item-tag">Settings</span>
              </div>
            </Link>

            <Link
              to="/logout"
              className="sb-item logout"
              onClick={() => setOpen(false)}
            >
              <div className="sb-item-icon">
                <FontAwesomeIcon icon={faRightFromBracket} />
              </div>
              <div className="sb-item-label">
                <span className="sb-item-name">Logout</span>
                <span className="sb-item-tag">Session</span>
              </div>
            </Link>
          </div>
        </nav>

        <hr className="sb-divider" />

        {/* User footer */}
        <div className="sb-footer">
          <div className="sb-user">
            <div className="sb-user-avatar">
              {user?.username?.substring(0, 1).toUpperCase() || "U"}
            </div>
            <div className="sb-user-info">
              <div className="sb-user-name">{user?.username || "User"}</div>
              <div className="sb-user-role">
                {user?.rbac_roles_details?.[0]?.name || user?.rbac_roles?.[0]?.name || "No Role"}
              </div>
            </div>
            <span className="sb-user-dots">···</span>
          </div>
        </div>
      </aside>

      {open && <div className="sb-overlay" onClick={() => setOpen(false)} />}
    </>
  );
}